import { useEffect, useState } from 'react';
import styles from './QuestionEditor.module.css';

const API_BASE = import.meta.env.VITE_API_URL || 'https://balanced-arena.davidoberger.workers.dev';

// All supported question types
const QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'true_false', label: 'True/False' },
  { value: 'multiple_select', label: 'Multiple Select' },
  { value: 'fill_blank', label: 'Fill in the Blank' },
  { value: 'cloze', label: 'Cloze (Fill Gaps)' },
  { value: 'matching', label: 'Matching' },
  { value: 'categorization', label: 'Categorization' },
  { value: 'sequence', label: 'Sequence/Ordering' },
  { value: 'numeric', label: 'Numeric Input' },
  { value: 'short_answer', label: 'Short Answer' },
  { value: 'chess_puzzle', label: 'Chess Puzzle' },
  // New Perseus-inspired types
  { value: 'expression', label: 'Math Expression' },
  { value: 'grapher', label: 'Graph Plotter' },
  { value: 'number_line', label: 'Number Line' },
  { value: 'matrix', label: 'Matrix' },
  { value: 'dropdown', label: 'Dropdown' },
];

interface QuestionData {
  id?: string;
  text: string;
  category: string;
  subcategory: string;
  question_type: string;
  correct_answer: string;
  options: string[];
  difficulty: number;
  discrimination: number;
  guessing: number;
  image_url: string;
  explanation: string;
  tags: string[];
  interactive_config: Record<string, unknown> | null;
}

const defaultQuestion: QuestionData = {
  text: '',
  category: '',
  subcategory: '',
  question_type: 'multiple_choice',
  correct_answer: '',
  options: ['', '', '', ''],
  difficulty: 0,
  discrimination: 1,
  guessing: 0.25,
  image_url: '',
  explanation: '',
  tags: [],
  interactive_config: null,
};

interface QuestionEditorProps {
  questionId: string | null;
  onSave: () => void;
  onCancel: () => void;
}

export function QuestionEditor({ questionId, onSave, onCancel }: QuestionEditorProps) {
  const [question, setQuestion] = useState<QuestionData>(defaultQuestion);
  const [categories, setCategories] = useState<{ name: string; subcategories: string[] }[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [tagInput, setTagInput] = useState('');

  // Load categories
  useEffect(() => {
    fetch(`${API_BASE}/api/questions/categories`)
      .then(r => r.json())
      .then(data => {
        const cats = data.map((c: { name: string; subcategories: { name: string }[] }) => ({
          name: c.name,
          subcategories: c.subcategories?.map((s: { name: string }) => s.name) || []
        }));
        setCategories(cats);
      })
      .catch(console.error);
  }, []);

  // Load existing question if editing
  useEffect(() => {
    if (questionId) {
      setLoading(true);
      fetch(`${API_BASE}/api/questions/${questionId}`)
        .then(r => r.json())
        .then(data => {
          setQuestion({
            id: data.id,
            text: data.text || '',
            category: data.category || '',
            subcategory: data.subcategory || '',
            question_type: data.question_type || 'multiple_choice',
            correct_answer: data.correct_answer || '',
            options: data.options || ['', '', '', ''],
            difficulty: data.difficulty ?? 0,
            discrimination: data.discrimination ?? 1,
            guessing: data.guessing ?? 0.25,
            image_url: data.image_url || '',
            explanation: data.explanation || '',
            tags: data.tags || [],
            interactive_config: data.interactive_config || null,
          });
        })
        .catch(err => setError(`Failed to load question: ${err.message}`))
        .finally(() => setLoading(false));
    }
  }, [questionId]);

  const updateField = <K extends keyof QuestionData>(field: K, value: QuestionData[K]) => {
    setQuestion(prev => ({ ...prev, [field]: value }));
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...question.options];
    newOptions[index] = value;
    setQuestion(prev => ({ ...prev, options: newOptions }));
  };

  const addOption = () => {
    setQuestion(prev => ({ ...prev, options: [...prev.options, ''] }));
  };

  const removeOption = (index: number) => {
    if (question.options.length <= 2) return;
    const newOptions = question.options.filter((_, i) => i !== index);
    setQuestion(prev => ({ ...prev, options: newOptions }));
  };

  const addTag = () => {
    if (tagInput.trim() && !question.tags.includes(tagInput.trim())) {
      setQuestion(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setQuestion(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleSave = async () => {
    if (!question.text.trim()) {
      setError('Question text is required');
      return;
    }
    if (!question.category) {
      setError('Category is required');
      return;
    }
    if (!question.correct_answer.trim()) {
      setError('Correct answer is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const method = questionId ? 'PUT' : 'POST';
      const url = questionId
        ? `${API_BASE}/api/questions/${questionId}`
        : `${API_BASE}/api/questions`;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: question.text,
          category: question.category,
          subcategory: question.subcategory || null,
          question_type: question.question_type,
          correct_answer: question.correct_answer,
          options: JSON.stringify(question.options.filter(o => o.trim())),
          difficulty: question.difficulty,
          discrimination: question.discrimination,
          guessing: question.guessing,
          image_url: question.image_url || null,
          explanation: question.explanation || null,
          tags: JSON.stringify(question.tags),
          interactive_config: question.interactive_config ? JSON.stringify(question.interactive_config) : null,
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to save question');
      }

      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const selectedCategory = categories.find(c => c.name === question.category);
  const subcategories = selectedCategory?.subcategories || [];

  if (loading) {
    return <div className={styles.loading}>Loading question...</div>;
  }

  return (
    <div className={styles.editor}>
      <div className={styles.editorHeader}>
        <h2 className={styles.editorTitle}>
          {questionId ? 'Edit Question' : 'Create Question'}
        </h2>
        <div className={styles.headerActions}>
          <button
            className={styles.previewButton}
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.editorContent}>
        <div className={styles.formSection}>
          {/* Question Type */}
          <div className={styles.field}>
            <label className={styles.label}>Question Type</label>
            <select
              value={question.question_type}
              onChange={(e) => updateField('question_type', e.target.value)}
              className={styles.select}
            >
              {QUESTION_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Category & Subcategory */}
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.label}>Category *</label>
              <select
                value={question.category}
                onChange={(e) => {
                  updateField('category', e.target.value);
                  updateField('subcategory', '');
                }}
                className={styles.select}
              >
                <option value="">Select category</option>
                {categories.map(c => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Subcategory</label>
              <select
                value={question.subcategory}
                onChange={(e) => updateField('subcategory', e.target.value)}
                className={styles.select}
                disabled={!question.category}
              >
                <option value="">None</option>
                {subcategories.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Question Text */}
          <div className={styles.field}>
            <label className={styles.label}>Question Text *</label>
            <textarea
              value={question.text}
              onChange={(e) => updateField('text', e.target.value)}
              className={styles.textarea}
              rows={4}
              placeholder="Enter the question text..."
            />
          </div>

          {/* Image URL */}
          <div className={styles.field}>
            <label className={styles.label}>Image URL (optional)</label>
            <input
              type="text"
              value={question.image_url}
              onChange={(e) => updateField('image_url', e.target.value)}
              className={styles.input}
              placeholder="https://..."
            />
          </div>

          {/* Options - for multiple choice types */}
          {['multiple_choice', 'multiple_select', 'dropdown'].includes(question.question_type) && (
            <div className={styles.field}>
              <label className={styles.label}>Answer Options</label>
              {question.options.map((opt, i) => (
                <div key={i} className={styles.optionRow}>
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => updateOption(i, e.target.value)}
                    className={styles.input}
                    placeholder={`Option ${i + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(i)}
                    className={styles.removeButton}
                    disabled={question.options.length <= 2}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button type="button" onClick={addOption} className={styles.addButton}>
                + Add Option
              </button>
            </div>
          )}

          {/* Correct Answer */}
          <div className={styles.field}>
            <label className={styles.label}>Correct Answer *</label>
            {question.question_type === 'true_false' ? (
              <select
                value={question.correct_answer}
                onChange={(e) => updateField('correct_answer', e.target.value)}
                className={styles.select}
              >
                <option value="">Select</option>
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            ) : ['multiple_choice', 'dropdown'].includes(question.question_type) ? (
              <select
                value={question.correct_answer}
                onChange={(e) => updateField('correct_answer', e.target.value)}
                className={styles.select}
              >
                <option value="">Select correct option</option>
                {question.options.filter(o => o.trim()).map((opt, i) => (
                  <option key={i} value={opt}>{opt}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={question.correct_answer}
                onChange={(e) => updateField('correct_answer', e.target.value)}
                className={styles.input}
                placeholder="Enter correct answer..."
              />
            )}
          </div>

          {/* Explanation */}
          <div className={styles.field}>
            <label className={styles.label}>Explanation (shown after answer)</label>
            <textarea
              value={question.explanation}
              onChange={(e) => updateField('explanation', e.target.value)}
              className={styles.textarea}
              rows={2}
              placeholder="Explain why this is the correct answer..."
            />
          </div>

          {/* IRT Parameters */}
          <div className={styles.fieldSection}>
            <h3 className={styles.sectionLabel}>IRT Parameters</h3>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.label}>Difficulty (θ)</label>
                <input
                  type="number"
                  step="0.1"
                  min="-3"
                  max="3"
                  value={question.difficulty}
                  onChange={(e) => updateField('difficulty', parseFloat(e.target.value) || 0)}
                  className={styles.input}
                />
                <span className={styles.hint}>-3 (easy) to +3 (hard)</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Discrimination (a)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="3"
                  value={question.discrimination}
                  onChange={(e) => updateField('discrimination', parseFloat(e.target.value) || 1)}
                  className={styles.input}
                />
                <span className={styles.hint}>Higher = better at separating skill levels</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Guessing (c)</label>
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  max="0.5"
                  value={question.guessing}
                  onChange={(e) => updateField('guessing', parseFloat(e.target.value) || 0)}
                  className={styles.input}
                />
                <span className={styles.hint}>Probability of guessing correctly</span>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className={styles.field}>
            <label className={styles.label}>Tags</label>
            <div className={styles.tagInput}>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className={styles.input}
                placeholder="Add a tag..."
              />
              <button type="button" onClick={addTag} className={styles.addButton}>Add</button>
            </div>
            <div className={styles.tags}>
              {question.tags.map(tag => (
                <span key={tag} className={styles.tag}>
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)}>×</button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Preview Panel */}
        {showPreview && (
          <div className={styles.previewPanel}>
            <h3 className={styles.previewTitle}>Preview</h3>
            <div className={styles.previewContent}>
              {question.image_url && (
                <img src={question.image_url} alt="Question" className={styles.previewImage} />
              )}
              <p className={styles.previewText}>{question.text || 'Question text...'}</p>
              {['multiple_choice', 'multiple_select', 'dropdown'].includes(question.question_type) && (
                <div className={styles.previewOptions}>
                  {question.options.filter(o => o.trim()).map((opt, i) => (
                    <div key={i} className={`${styles.previewOption} ${opt === question.correct_answer ? styles.correct : ''}`}>
                      {opt}
                    </div>
                  ))}
                </div>
              )}
              {question.explanation && (
                <div className={styles.previewExplanation}>
                  <strong>Explanation:</strong> {question.explanation}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={styles.editorFooter}>
        <button onClick={onCancel} className={styles.cancelButton} disabled={saving}>
          Cancel
        </button>
        <button onClick={handleSave} className={styles.saveButton} disabled={saving}>
          {saving ? 'Saving...' : questionId ? 'Update Question' : 'Create Question'}
        </button>
      </div>
    </div>
  );
}
